import { makeObservable, observable, action } from "mobx";

import { api } from "../utils/Api";
import {
  createTable,
  getWorksheet,
  getTable,
  clearTable,
  initRecordsInTable,
  reapplyAutoFilter,
  removeAutoFilter,
  createWorksheet,
  activateWorksheet,
  resizeHeaders,
} from "../utils/ExcelRun";
import { currentSheetHasReport, getRangeHeader, saveReportGenerated } from "../utils/PwOffice";
import UIStore from "./UIStore";
import { Parameter, ParameterPayload } from '../types';

export type ImportState = "idle" | "loading" | "success" | "error";
export default class ImportStore {
  private currentAbortController: AbortController | null = null;

  @observable importState: ImportState = "idle";
  @observable errorMessage = "";
  @observable percentage = 0;
  @observable stepLabel = "";
  @observable showDialogConfirm = false;
  @observable parameters: Parameter[] = [];
  @observable getInParameters = false;
  uiStore: UIStore;

  constructor(uiStore: UIStore) {
    this.uiStore = uiStore;
    makeObservable(this);
  }

  @action resetImportState() {
    this.importState = "idle";
    this.errorMessage = "";
    this.percentage = 0;
    this.stepLabel = "";
  }

  @action setError(message: string) {
    this.importState = "error";
    this.errorMessage = message;
    setTimeout(
      action(() => {
        this.importState = "idle";
      }),
      5000
    );
  }

  @action setSuccess() {
    this.importState = "success";
    this.stepLabel = "¡Proceso de importación completado!";
    setTimeout(
      action(() => {
        this.importState = "idle";
      }),
      5000
    );
  }

  transformData(records: Record<string, unknown>[]): unknown[][] {
    let structure = Object.keys(records[0]);
    let recordsMapped: unknown[][] = [];

    for (let index = 0; index < records.length; index++) {
      const record = records[index];
      let recordMapped: unknown[] = [];

      for (let structureIndex = 0; structureIndex < structure.length; structureIndex++) {
        const key = structure[structureIndex];
        recordMapped.push(record[key]);
      }

      recordsMapped.push(recordMapped);
    }

    return recordsMapped;
  }

  @action dismissDialogConfirm() {
    this.showDialogConfirm = false;
  }

  @action async validateTable(parameters: ParameterPayload[]) {
    const worksheetName = this.uiStore.sheetTitle.SheetName;
    const reportExisting = await currentSheetHasReport();
    const worksheet = await getWorksheet(worksheetName);

    let table: Excel.Table | null = await getTable(worksheet);
    if (table) {
      const { reportSelected, forceReplaceSheet } = this.uiStore;

      if (reportExisting && (reportSelected?.key === reportExisting.ReportInfo.key || forceReplaceSheet)) {
        this.uiStore.setTextForUpdateTable();
      } else {
        this.uiStore.setTextForCreateSheet();
      }
      this.showDialogConfirm = true;
    } else {
      await this.getData(parameters);
    }
  }

  @action async getParameters() {
    if (!this.uiStore?.reportSelected || !this.uiStore?.reportSelected.key) {
      // this.uiStore?.changeSection('import');
      return;
    }

    this.getInParameters = true;
    const reportExisting = await currentSheetHasReport();
    if (reportExisting && reportExisting.ReportInfo.key === this.uiStore.reportSelected.key) {
      this.uiStore.setParameterSaved(reportExisting.Parameters);
    }
    const parameters: Parameter[] = await api
      .Get(`ODataReportParameters/GetParameters/${this.uiStore?.reportSelected!.key}`)
      .then(({ data }) => data)
      .catch((err) => []);

    if (this.uiStore?.parametersSaved && this.uiStore?.parametersSaved.length > 0) {
      parameters.forEach((parameter) => {
        const existParameter = this.uiStore.parametersSaved.find((x) => x.Name === parameter.Name);
        if (existParameter) {
          parameter.Value = existParameter.Type === "Date" && existParameter.Value ? new Date(existParameter.Value as string) : existParameter.Value;
        }
        parameter.errorMessage = "";
      });
    }
    this.getInParameters = false;
    this.parameters = parameters;
    return parameters;
  }

  @action async getData(parameters: ParameterPayload[]) {
    // Abort any previous in-flight request
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }
    this.currentAbortController = new AbortController();
    const { signal } = this.currentAbortController;

    this.resetImportState();
    this.showDialogConfirm = false;
    this.importState = "loading";
    this.stepLabel = "Iniciando importación";
    let records: Record<string, unknown>[] = [];

    try {
      const reportExisting = await currentSheetHasReport();
      const reportName = this.uiStore.reportSelected?.tableName || reportExisting?.ReportInfo?.tableName || "";

      if (reportName.toLowerCase().includes("stream")) {
        let recordCount = 0;

        await api.Stream(`ODataReports/GenerateDataInReport/${this.uiStore.reportSelected!.key}/stream`, {
          method: "POST",
          body: parameters,
          signal,
          onData: (data) => {
            records.push(data);
            recordCount++;
            this.setPercentageInfo(recordCount - 1, -1);
          },
          onError: () => {
            throw Error();
          },
        });
      } else {
        this.stepLabel = "Importando información desde Peopleware";
        records = await api
          .Post(`ODataReports/GenerateDataInReport/${this.uiStore.reportSelected!.key}`, parameters, signal)
          .then(({ data }) => data);
      }

      const totalOfRecords = records.length;

      if (totalOfRecords === 0) {
        this.uiStore.showEmptyDialog = true;
        this.importState = "idle";
        return;
      }

      this.stepLabel = `Insertando ${totalOfRecords} registros en Excel`;
      const worksheetName = this.uiStore.sheetTitle.SheetName;
      let worksheet: Excel.Worksheet;

      if (reportExisting && reportExisting.ReportInfo.key !== this.uiStore.reportSelected?.key) {
        let name = `${Date.now()}${this.uiStore.reportSelected!.text}`;
        if (name.length > 31) {
          name = name.slice(0, 31);
        }
        worksheet = await createWorksheet(name);
      } else {
        worksheet = await getWorksheet(worksheetName);
      }

      let table: Excel.Table | null = await getTable(worksheet);
      let rangeDescriptor = "";
      let headers: string[] = [];
      let newTable = false;

      if (totalOfRecords > 0) {
        let firstRecord = records[0];
        rangeDescriptor = getRangeHeader(firstRecord);
        headers = Object.keys(firstRecord);
      }

      const data = this.transformData(records);

      if (table) {
        await resizeHeaders(table, headers, worksheet);
      } else {
        newTable = true;
        table = await createTable(worksheet, rangeDescriptor, headers, this.uiStore.reportSelected!.tableName);
      }

      if (!newTable) {
        try {
          await clearTable(table);
        } catch (error) {
          await removeAutoFilter(table);
          await clearTable(table);
        }
      }

      await initRecordsInTable(table, data);

      if (!newTable) {
        await reapplyAutoFilter(table);
      }

      await saveReportGenerated(worksheet, parameters, this.uiStore.reportSelected!);
      await activateWorksheet(worksheet);
      await this.uiStore.checkIfExistsReport();
      this.currentAbortController = null;

      setTimeout(() => {
        this.setSuccess();
      }, 5000);
    } catch (e) {
      if (e instanceof Error && (e.name === "AbortError" || e.name === "CanceledError")) {
        return;
      }
      this.setError("Ocurrio un error durante el proceso de importación, favor de intentarlo más tarde");
    }
  }

  @action setPercentageInfo(step: number, total: number) {
    if (total > 0) {
      this.percentage = step / total;
      this.stepLabel = `${step} / ${total} registros importados desde Peopleware`;
    } else {
      this.percentage = 0;
      this.stepLabel = `${step} registros recibidos desde Peopleware...`;
    }
  }
}
